import React, {useState, useEffect, useCallback} from 'react';
import {withTranslation} from 'react-i18next';
import {Form, Field} from 'react-final-form';
import {Panel} from "primereact/panel";
import axios from "axios";
import Cookies from "js-cookie";
import {Badge} from "primereact/badge";
import currencyFormatter from "currency-formatter";
import {classNames} from 'primereact/utils';
import {InputText} from 'primereact/inputtext';
import {Button} from 'primereact/button';
import {Dropdown} from "primereact/dropdown";
import {BlockUI} from "primereact/blockui";

import FormLocker from "../../util/FormLocker";
import moment from "moment/moment";
import {Calendar} from "primereact/calendar";
import {addLocale} from "primereact/api";
import {InputMask} from "primereact/inputmask";
import {InputSwitch} from "primereact/inputswitch";
import {InputNumber} from "primereact/inputnumber";

const RegistrationsFormComponent = (props) => {

  const {t} = props;
  const [formData, setFormData] = useState({});
  const [newFormData, setNewFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [locking, setLocking] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionplan, setSubscriptionplan] = useState([]);
  const [subscriptionplanActive, setSubscriptionplanActive] = useState(null);
  const [pendingSubscription, setPendingSubscription] = useState(null);
  const [allowedActions, setAllowedActions] = useState([]);

  const subscriptionHeaders = useCallback(() => ({
    'Authorization': 'Bearer ' + Cookies.get("userToken"),
    "Sys-name": Cookies.get("instanceId"),
    "Preferred-locale": Cookies.get("language")
  }), []);

  const subscriptions = useCallback(() => {
    setSubscriptionLoading(true);

    const activeSubscriptionRequest = axios({
      method: 'get',
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "system/settings/subscription",
      params: {},
      headers: subscriptionHeaders()
    });

    const subscriptionPlansRequest = axios({
      method: 'get',
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "subscription-plan",
      params: {},
      headers: subscriptionHeaders()
    });

    Promise.allSettled([activeSubscriptionRequest, subscriptionPlansRequest])
      .then(([subscriptionResult, subscriptionPlanResult]) => {
        let activePlanId = null;
        const pendingPlanIds = [];

        if (subscriptionResult.status === 'fulfilled') {
          const subscriptionData = subscriptionResult.value.data.data || null;
          setSubscriptionplanActive(subscriptionData);
          setPendingSubscription(subscriptionData?.pendingSubscription || null);
          setAllowedActions(Array.isArray(subscriptionData?.allowedActions) ? subscriptionData.allowedActions : []);
          activePlanId = subscriptionData?.subscription_plan?.id || null;

          if (Array.isArray(subscriptionData?.pendingSubscription)) {
            subscriptionData.pendingSubscription.forEach((item) => {
              const planId = item?.planId || item?.id || null;
              if (planId) {
                pendingPlanIds.push(planId);
              }
            });
          } else if (subscriptionData?.pendingSubscription) {
            const planId = subscriptionData.pendingSubscription?.planId || subscriptionData.pendingSubscription?.id || null;
            if (planId) {
              pendingPlanIds.push(planId);
            }
          }
        } else {
          setSubscriptionplanActive(null);
          setPendingSubscription(null);
          setAllowedActions([]);
        }

        if (subscriptionPlanResult.status === 'fulfilled') {
          const subscriptionPlans = subscriptionPlanResult.value.data.data.items || [];
          const filteredPlans = subscriptionPlans.filter((plan) => {
            if (plan?.visible_for_plan_list) {
              return true;
            }
            if (activePlanId && plan?.id === activePlanId) {
              return true;
            }
            return pendingPlanIds.includes(plan?.id);
          });
          setSubscriptionplan(filteredPlans);
        } else {
          window.App.toastShow('error', t('error'), subscriptionPlanResult.reason.toString(), '', subscriptionPlanResult.reason.response);
        }
      })
      .finally(() => {
        setSubscriptionLoading(false);
      });
  }, [subscriptionHeaders, t]);

  const hasActiveSubscription = () => {
    return subscriptionplanActive?.status === 'ACTIVE' && subscriptionplanActive?.subscription_plan?.id;
  };

  const isActivePlan = (plan) => {
    return hasActiveSubscription() && subscriptionplanActive.subscription_plan.id === plan.id;
  };

  const isEndingPlan = (plan) => {
    if (Array.isArray(pendingSubscription)) {
      return pendingSubscription.some((item) => (item?.planId || item?.id) === plan.id);
    }
    return (pendingSubscription?.planId || pendingSubscription?.id) === plan.id;
  };

  const hasPendingSubscription = () => {
    if (Array.isArray(pendingSubscription)) {
      return pendingSubscription.some((item) => !!(item?.planId || item?.id));
    }
    return !!(pendingSubscription?.planId || pendingSubscription?.id);
  };

  const hasAllowedAction = (action) => {
    return allowedActions.includes(action);
  };

  const formatPrice = (plan) => {
    return currencyFormatter.format(
      plan.amount, {
        precision: t('currency_precision'),
        symbol: t('currency_symbol'),
        decimal: t('currency_decimal'),
        thousand: t('currency_thousand'),
      }
    );
  };

  const formatFrequency = (frequency) => {
    if (frequency === 'MONTHLY') {
      return t('monthly');
    }
    if (frequency === 'YEARLY') {
      return t('yearly');
    }
    if (frequency === 'VARIED') {
      return t('varied');
    }
    return frequency;
  };

  const formatDate = (date) => {
    return moment.utc(date).local().format(t('YYYY-MM-DD'));
  };

  const subscriptionApiCall = (method, url, data = {}) => {
    return axios({
      method,
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: subscriptionHeaders()
    });
  };

  const onCancelSubscription = () => {
    setSubscriptionLoading(true);
    subscriptionApiCall('post', 'system/settings/subscription/cancel', {})
      .then((response) => {
        window.App.toastShow('success', t('success'), response.data.message);
        subscriptions();
      })
      .catch((error) => {
        setSubscriptionLoading(false);
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  };

  const onPauseSubscription = () => {
    setSubscriptionLoading(true);
    subscriptionApiCall('post', 'system/settings/subscription/pause', {})
      .then((response) => {
        window.App.toastShow('success', t('success'), response.data.message);
        subscriptions();
      })
      .catch((error) => {
        setSubscriptionLoading(false);
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  };

  const onSubscribe = (planId) => {
    setSubscriptionLoading(true);
    subscriptionApiCall('post', 'system/settings/subscription/subscribe/' + planId, {})
      .then((response) => {
        window.App.toastShow('success', t('success'), response.data.message);
        subscriptions();
      })
      .catch((error) => {
        setSubscriptionLoading(false);
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  };

  const validate = (data) => {
    let errors = {};
    return errors;
  };
  const onSubmit = (data, form) => {
    setLoading(true)
    if (formData && formData.id) {
      axios.patch(process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "system-admin/user-registration/" + formData.id,
        newFormData,
        {
          params: {},
          headers: {
            'Authorization': 'Bearer ' + Cookies.get("userToken"),
            "Sys-name": Cookies.get("instanceId"),
            "Preferred-locale": Cookies.get("language")
          }
        })
        .then(response => {
          setFormData(response.data.data);
          setNewFormData({});
          setLoading(false)
          window.App.toastShow('success', t('success'), response.data.message)
          window.App.setState({
            rerenderRegistrationsList: true,
          })
          if (props.closeForm === true) {
            window.App.setState({
              sidebarRegistrationsForm: false,
            })
          }
        })
        .catch(error => {
          setLoading(false)
          window.App.toastShow('error', t('error'), error.response.data.message, '', error.response)
        })
    } else {
      axios.post(process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "system-admin/user-registration",
        formData,
        {
          params: {},
          headers: {
            'Authorization': 'Bearer ' + Cookies.get("userToken"),
            "Sys-name": Cookies.get("instanceId"),
            "Preferred-locale": Cookies.get("language")
          }
        })
        .then(response => {
          setFormData(response.data.data)
          setLoading(false)
          window.App.toastShow('success', t('success'), response.data.message)
          window.App.setState({
            rerenderRegistrationsList: true,
          })
          if (props.closeForm === true) {
            window.App.setState({
              sidebarRegistrationsForm: false,
            })
          }
        })
        .catch(error => {
          setLoading(false)
          window.App.toastShow('error', t('error'), error.response.data.message, '', error.response)
        })
    }
    form.restart();
  };
  const isFormFieldValid = (meta) => !!(meta.touched && meta.error);
  const getFormErrorMessage = (meta) => {
    return isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>;
  };

  FormLocker(() => {
    if (formData && formData.id && formData._class_name) {
      window.App.formLock(formData._class_name, formData.id)
    }
  }, 1000);

  addLocale('default', {
    firstDayOfWeek: 1,
    dayNames: [t('sunday'), t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday'), t('saturday')],
    dayNamesShort: [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')],
    dayNamesMin: [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')],
    monthNames: [t('jan'), t('feb'), t('mar'), t('apr'), t('may'), t('jun'), t('jul'), t('aug'), t('sep'),
      t('oct'), t('nov'), t('dec')],
    monthNamesShort: [t('jan'), t('feb'), t('mar'), t('apr'), t('may'), t('jun'), t('jul'), t('aug'), t('sep'),
      t('oct'), t('nov'), t('dec')],
    today: t('today'),
    clear: t('delete')
  });

  useEffect(() => {
    subscriptions();
  }, [subscriptions]);

  useEffect(() => {
    if (window.App.state.propsRegistrations && window.App.state.propsRegistrations.id) {
      setLoading(true)
      axios({
        method: 'get',
        url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "system-admin/user-registration/" + window.App.state.propsRegistrations.id,
        params: {},
        headers: {
          'Authorization': 'Bearer ' + Cookies.get("userToken"),
          "Sys-name": Cookies.get("instanceId"),
          "Preferred-locale": Cookies.get("language")
        }
      })
        .then(response => {
          setLoading(false)
          setFormData(response.data.data);
          if (response.data && response.data.data && response.data.data.under_edit_from && response.data.data.under_edit_by !== Number(Cookies.get('userId'))) {
            setLocking(true)
          }
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
          setLoading(false)
        })
    }
  }, [t])

  return (
    <>
      <BlockUI blocked={locking} className="blocked-ui-overlay" template={<div className="blocked-ui-message"><i className="pi pi-exclamation-triangle blocked-ui-message-icon" aria-hidden="true"></i>{t("module_editing_after_save_only")}</div>}>
        <Form onSubmit={onSubmit} initialValues={formData}
              validate={validate} render={({handleSubmit}) => (
          <form onSubmit={handleSubmit} className="">
            <Panel className={"p-mt-3"}>
              <div className={"p-grid"}>
                <div className={"p-col-12 p-lg-6 p-col-align-center"}>
                  <h2 className={"p-m-0"}>{t('registrations_form')}</h2>
                </div>
                <div className={"p-col-12 p-lg-6 p-text-right p-col-align-center"}>
                  <Button type="submit" aria-label={t('save')} icon={"pi pi-save"}
                          loading={loading}
                          onClick={() => {
                            props = {...props, closeForm: false}
                          }}
                          className="p-button-success p-ml-2"/>
                  <Button type="submit" aria-label={t('save_and_close')}
                          loading={loading}
                          onClick={() => {
                            props = {...props, closeForm: true}
                          }}
                          className="p-button-success p-ml-2">
              <span>
                <i className={"pi pi-save"}></i>&nbsp;&nbsp;<i className={"pi pi-backward"}></i>
              </span>
                  </Button>
                </div>
              </div>
              <div className={"p-grid"}>

                <div className={"p-col-12 p-lg-6"}>
                  <Field name="email" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="email"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('email')}</label>
                      <InputText id="email" {...input}
                                 onChange={(e) => {
                                   setFormData({...formData, email: e.target.value});
                                   setNewFormData({...newFormData, email: e.target.value});
                                 }}
                                 value={formData.email}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
                <div className={"p-col-12 p-lg-6"}>
                  <Field name="invoice_email" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="invoice_email"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('invoice_email')}</label>
                      <InputText id="invoice_email" {...input}
                                 onChange={(e) => {
                                   setFormData({...formData, invoice_email: e.target.value});
                                   setNewFormData({...newFormData, invoice_email: e.target.value});
                                 }}
                                 value={formData.invoice_email}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
              </div>
              <div className={"p-grid"}>
                <div className={"p-col-12 p-lg-6"}>
                  <Field name="annual_fee_start" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="annual_fee_start"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('annual_fee_start')}</label>
                      <div className="p-inputgroup calendar-icon-only">
                        <InputMask id={"annual_fee_start"}
                                   mask={t('9999-99-99')}
                                   value={formData.annual_fee_start}
                                   onChange={(e) => {
                                     if (e.value.indexOf("_") === -1) {
                                       if (e.value === '') {
                                         e.value = null
                                       }
                                       setFormData({
                                         ...formData,
                                         annual_fee_start: moment.utc(e.value).local().format(t('YYYY-MM-DD'))
                                       });
                                       setNewFormData({
                                         ...newFormData,
                                         annual_fee_start: moment.utc(e.value).local().format(t('YYYY-MM-DD'))
                                       });
                                     }
                                   }}
                                   className={classNames({'p-invalid': isFormFieldValid(meta)})}
                        />
                        <Calendar value={formData.annual_fee_start ? moment(formData.annual_fee_start, 'YYYY-MM-DD').toDate() : null}
                                  locale="default"
                                  dateFormat={t('YY-MM-DD')}
                                  showIcon
                                  onChange={(e) => {
                                    setFormData({
                                      ...formData,
                                      annual_fee_start: moment.utc(e.value).local().format(t('YYYY-MM-DD'))
                                    });
                                    setNewFormData({
                                      ...newFormData,
                                      annual_fee_start: moment.utc(e.value).local().format(t('YYYY-MM-DD'))
                                    });
                                  }}/>
                      </div>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
                <div className={"p-col-12 p-lg-3"}>
                  <Field name="annual_net_fee" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="annual_net_fee"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('annual_net_fee')}</label>
                      <span className="p-d-block">
                      <InputText id="annual_net_fee" {...input}
                                 keyfilter={"num"}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     annual_net_fee: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     annual_net_fee: e.target.value
                                   })
                                 }}
                                 value={formData.annual_net_fee}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
                <div className={"p-col-12 p-lg-3"}>
                  <Field name="annual_net_cap" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="annual_net_fee"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('annual_net_cap')}</label>
                      <span className="p-d-block">
                      <InputText id="annual_net_cap" {...input}
                                 keyfilter={"num"}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     annual_net_cap: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     annual_net_cap: e.target.value
                                   })
                                 }}
                                 value={formData.annual_net_cap}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
                <div className={"p-col-12 p-lg-3"}>
                  <Field name="monthly_net_cap" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="monthly_net_cap"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('monthly_net_cap')}</label>
                      <span className="p-d-block">
                      <InputText id="monthly_net_cap" {...input}
                                 keyfilter={"num"}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     monthly_net_cap: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     monthly_net_cap: e.target.value
                                   })
                                 }}
                                 value={formData.monthly_net_cap}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
                <div className={"p-col-12 p-lg-3"}>
                  <Field name="discount_percentage" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="discount_percentage"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('discount_percentage')}</label>
                      <span className="p-d-block">
                      <InputText id="discount_percentage" {...input}
                                 keyfilter={"num"}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     discount_percentage: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     discount_percentage: e.target.value
                                   })
                                 }}
                                 value={formData.discount_percentage}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
                <div className={"p-col-12 p-lg-3"}>
                  <Field name="fixed_monthly_fee" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="fixed_monthly_fee"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('fixed_monthly_fee')}</label>
                      <span className="p-d-block">
                      <InputText id="fixed_monthly_fee" {...input}
                                 keyfilter={"num"}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     fixed_monthly_fee: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     fixed_monthly_fee: e.target.value
                                   })
                                 }}
                                 value={formData.fixed_monthly_fee}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
              </div>
              <div className={"p-grid"}>
                <div className={"p-col-12 p-lg-4"}>
                  <Field name="merged_accommodation" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="merged_accommodation"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('merged_accommodation')}</label>
                      <span className="p-d-block">
                        <InputSwitch
                          className={"p-mt-1"}
                          name={"automatic_daily_close_case_of_out_of_order"}
                          checked={formData.merged_accommodation}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              merged_accommodation: e.value
                            })
                            setNewFormData({
                              ...newFormData,
                              merged_accommodation: e.value
                            })
                          }}/>
                      </span>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
                <div className={"p-col-12 p-lg-4"}>
                  <Field name="enabled" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="enabled"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('active')}</label>
                      <span className="p-d-block">
                        <InputSwitch
                          disabled={formData.email === localStorage.getItem('userEmail')}
                          className={"p-mt-1"}
                          name={"enabled"}
                          checked={formData.enabled}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              enabled: e.value
                            })
                            setNewFormData({
                              ...newFormData,
                              enabled: e.value
                            })
                          }}/>
                      </span>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
                <div className={"p-col-12 p-lg-4"}>
                  <Field name="allowed_to_change_subscription" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="allowed_to_change_subscription"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('allowed_to_change_subscription')}</label>
                      <span className="p-d-block">
                        <InputSwitch
                          className={"p-mt-1"}
                          name={"allowed_to_change_subscription"}
                          checked={formData.allowed_to_change_subscription}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              allowed_to_change_subscription: e.value
                            })
                            setNewFormData({
                              ...newFormData,
                              allowed_to_change_subscription: e.value
                            })
                          }}/>
                      </span>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
                <div className={"p-col-12 p-lg-6"}>
                  <Field name="merged_accommodation_invoice_to" render={({input, meta}) => (
                    <div className="p-field p-fluid">
                      <label htmlFor="merged_accommodation_invoice_to"
                             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('merged_accommodation_invoice_to')}</label>
                      <InputText id="merged_accommodation_invoice_to" {...input}
                                 onChange={(e) => {
                                   setFormData({...formData, merged_accommodation_invoice_to: e.target.value});
                                   setNewFormData({...newFormData, merged_accommodation_invoice_to: e.target.value});
                                 }}
                                 value={formData.merged_accommodation_invoice_to}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                      {getFormErrorMessage(meta)}
                    </div>
                  )}/>
                </div>
              </div>
            </Panel>

            <Panel className={"p-mt-3"}>
              <div className={"p-grid"}>
                <div className={"p-col-12 p-lg-6 p-col-align-center"}>
                  <h2 className={"p-m-0"}>
                    {t('subscriptions')}
                  </h2>
                </div>
                <div className={"p-col-12 p-lg-6 p-text-right p-col-align-center"}>

                </div>
              </div>
              {subscriptionplan && subscriptionplan[0] ? (
                <div className={"subscription-plans"}>
                  {subscriptionplan.map((plan) => {
                    const activePlan = isActivePlan(plan);
                    const endingPlan = isEndingPlan(plan);
                    const canChangePlan = hasAllowedAction('change_subscription_plan');
                    const canCancelSubscription = hasAllowedAction('cancel_subscription');
                    const canPauseSubscription = hasAllowedAction('pause');

                    if (!activePlan && !canChangePlan) {
                      return null;
                    }

                    return (
                      <div key={plan.id} className={"subscription-plan" + (activePlan ? " subscription-plan--active" : "")}
                           style={{borderRadius: '4px'}}>
                        <div className={"p-grid p-m-0  subscription-plan__grid"}>
                          <div className={"p-col-8  p-col-align-center subscription-plan__name-col"}>
                            <h4>
                              {plan.name}
                              {activePlan &&
                                <Badge value={hasPendingSubscription() ? t('subscription_cancellation_pending') : t('active')}
                                       severity={hasPendingSubscription() ? "warning" : "success"}
                                       style={{position: 'relative', top: '-4px'}}
                                       className={"p-ml-2"}></Badge>
                              }
                              {!activePlan && endingPlan &&
                                <Badge value={t('subscription_in_progress')} severity={"warning"}
                                       style={{position: 'relative', top: '-4px'}}
                                       className={"p-ml-2"}></Badge>
                              }
                            </h4>
                          </div>
                          <div
                            className={"p-col-4  p-col-align-center p-text-right subscription-plan__period-col p-text-nowrap"}>
                            {activePlan &&
                              <div className={"subscription-plan__period"}>
                                <span className={"p-m-0 p-p-0"} style={{lineHeight: '8px'}}>
                                  <small>{t('subscription_end_date')}</small>
                                  {formatDate(subscriptionplanActive.next_payment_date)}
                                </span>
                              </div>
                            }
                          </div>
                        </div>
                        {plan.code !== 'FREE_S' &&
                          <div className={"subscription-plan__grid subscription-plan__grid--usage"}>
                            <span className={"subscription-plan__usage-item"}>
                              <small style={{fontWeight: "normal"}}>{plan.public_description}</small>
                            </span>
                          </div>
                        }
                        <div className={"p-grid p-m-0  subscription-plan__grid subscription-plan__grid--actions"}>
                          <div className={"p-col-12  p-lg-4 p-col-align-center subscription-plan__code-col"}>
                            <div className={"subscription-plan__code"}>{plan.code}</div>
                          </div>
                          <div className={"p-col-12  p-lg-3 p-col-align-center subscription-plan__price-col"}>
                            <div className={"subscription-plan__price"}>
                              <span>{formatPrice(plan)}</span>
                              <small>{plan.currency} / {formatFrequency(plan.frequency)}</small>
                            </div>
                          </div>
                          <div className={"p-col-12  p-lg-5 p-col-align-center p-text-right subscription-plan__action-col"}>
                            {activePlan ? (
                              <div style={{display: 'flex', gap: '0.5rem', width: '100%'}}>
                                <Button type={"button"} icon={"pi pi-times"} label={"Befejezés"}
                                        className={"p-button-danger p-d-block"}
                                        style={{flex: 1}}
                                        loading={subscriptionLoading}
                                        disabled={hasPendingSubscription() || !canCancelSubscription}
                                        onClick={onCancelSubscription}
                                />
                                <Button type={"button"} icon={"pi pi-pause"} label={"Felfüggesztés"}
                                        className={"p-button-warning p-d-block"}
                                        style={{flex: 1}}
                                        loading={subscriptionLoading}
                                        disabled={hasPendingSubscription() || !canPauseSubscription}
                                        onClick={onPauseSubscription}
                                />
                              </div>
                            ) : endingPlan ? (
                              <Button type={"button"} icon={"pi pi-clock"} label={t('subscription_in_progress')}
                                      className={"p-button-secondary p-d-block w-100"}
                                      disabled={true}
                              />
                            ) : (
                              <Button type={"button"} icon={"pi pi-check"} label={"Aktiválás"}
                                      className={"p-button-success p-d-block w-100"} loading={subscriptionLoading}
                                      onClick={() => {
                                        onSubscribe(plan.id);
                                      }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={"subscription-plans__empty"}>{t('empty_message')}</div>
              )}
            </Panel>

          </form>
        )}/>
      </BlockUI>
      {locking === true &&
        <div className={"p-mt-2"}>
          <i className={"pi pi-exclamation-circle"}></i>
          &nbsp;
          {t('form_under_edit')}
          {formData && formData.under_edit_by_name_snap &&
            <>&nbsp;{t('form_under_edit_by')}: {formData.under_edit_by_name_snap}</>
          }
        </div>
      }
    </>
  )
}
export default withTranslation('common')(RegistrationsFormComponent);
